import { useState } from 'react';

import { HIDE_HISTORY_LABEL } from './brief-copy.ts';
import { dateOf } from './dates.ts';

/** One version of a document, with the sentence saying why it exists and when it was made. */
export interface VersionHistoryEntry {
  version: number;
  /** Why this version exists; empty when nothing was recorded. */
  reasonText: string;
  /** When the version was made, as an ISO timestamp. */
  at: string;
}

/** A step read's history, oldest first, as `VersionHistory` takes it. */
export const historyEntriesOf = (
  history: readonly { version: number; reasonText: string; createdAt: string }[],
): VersionHistoryEntry[] =>
  history.map(({ version, reasonText, createdAt }) => ({ version, reasonText, at: createdAt }));

export interface VersionHistoryProps {
  /** Prefixes every test id and element id, so one step's history never collides with another's. */
  testIdPrefix: string;
  /** The version on screen and why it exists; `reasonText` is empty when nothing was recorded. */
  current: { version: number; reasonText: string };
  /** Every version, oldest first, as the step's read returns them. Shown newest first. */
  entries: readonly VersionHistoryEntry[];
}

/**
 * A document's version and history behind one toggle on its own line.
 *
 * Closed, it is only the toggle: the version number is bookkeeping a researcher looks for when
 * they are looking back, so it is shown with the history rather than over the document. Open, it
 * names the version on screen and why it exists, then every version newest first.
 */
export const VersionHistory = ({ testIdPrefix, current, entries }: VersionHistoryProps) => {
  const [open, setOpen] = useState(false);
  const panelId = `${testIdPrefix}-history-panel`;

  return (
    <div className="version-history">
      <button
        type="button"
        className="version-history-toggle text-link muted"
        aria-expanded={open}
        aria-controls={panelId}
        data-test-id={`${testIdPrefix}-history-toggle`}
        onClick={() => setOpen((was) => !was)}
      >
        {open ? HIDE_HISTORY_LABEL : 'History'}
      </button>
      {open && (
        <div id={panelId}>
          <p className="artifact-meta" data-test-id={`${testIdPrefix}-version`}>
            Version {current.version}
            {current.reasonText === '' ? '' : ` · ${current.reasonText}`}
          </p>
          <ol
            className="version-history-list"
            id={`${testIdPrefix}-history`}
            data-test-id={`${testIdPrefix}-history`}
          >
            {entries.toReversed().map((entry) => (
              <li
                key={entry.version}
                data-test-id={`${testIdPrefix}-history-${String(entry.version)}`}
              >
                Version {entry.version}
                {entry.reasonText === '' ? '.' : `: ${entry.reasonText}.`}{' '}
                <span className="artifact-meta">{dateOf(entry.at)}</span>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
};
